import type { ReactElement } from 'react'

// Row 7 (D3/D4): value labels inside bars/segments, v2's mechanism reflected (recharts LabelList
// custom content). A bar too small for an INSIDE label no longer goes silent (D4 symmetric
// doctrine): it gets an OUTSIDE label placed by sign instead. Only stacked segments (which pass
// allowOutside: false — an outside label there would sit ON the neighbouring segment) and slots
// too narrow to host a readable outside label degrade to tooltip-only, never overlapping label
// soup (spec §5).

export const MIN_LABEL_HEIGHT = 18
export const MIN_LABEL_WIDTH = 32
export const OUTSIDE_GAP = 4
export const OUTSIDE_MIN_SLOT = 28
const HALO_FILTER = 'drop-shadow(0 0 2px rgba(255,255,255,0.85))'

interface LabelGeom {
  x?: number | string
  y?: number | string
  width?: number | string
  height?: number | string
  value?: number | string
}

interface RenderBarValueLabelOpts {
  /** Stacked segments pass false: an outside label there would land on the next segment. */
  allowOutside?: boolean
}

export function renderBarValueLabel(
  props: LabelGeom,
  format: (n: number) => string,
  opts?: RenderBarValueLabelOpts,
): ReactElement | null {
  const xRaw = Number(props.x ?? 0)
  const yRaw = Number(props.y ?? 0)
  const widthRaw = Number(props.width ?? 0)
  const heightRaw = Number(props.height ?? 0)
  // recharts passes below-zero bars as y-at-value-end with NEGATIVE height (measured live,
  // FU-50 fix round 2026-08-04); normalize to visual-top + magnitude so both sign
  // conventions land identically. Same for leftward bars' negative width in the
  // horizontal variant below.
  const height = Math.abs(heightRaw)
  const y = heightRaw < 0 ? yRaw + heightRaw : yRaw
  const width = Math.abs(widthRaw)
  const x = widthRaw < 0 ? xRaw + widthRaw : xRaw
  // recharts can pass value as a STRING in some configurations — coerce instead of suppressing.
  // The `!== ''` guard matters: Number('') === 0 would render a spurious 0-label.
  // recharts can also pass value as [lo, hi] for range bars → Number([lo,hi]) is NaN → label suppressed (intended: no range bars exist; revisit if they appear).
  const rawValue = Number(props.value)
  const value =
    props.value != null && props.value !== '' && !Number.isNaN(rawValue) ? rawValue : undefined
  if (value === undefined) return null

  const fitsInside = height >= MIN_LABEL_HEIGHT && width >= MIN_LABEL_WIDTH
  if (fitsInside) {
    return (
      <text
        x={x + width / 2}
        y={y + height / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={12}
        fontWeight={600}
        fill="#1a1a1a"
        style={{ filter: HALO_FILTER, pointerEvents: 'none' }}
      >
        {format(value)}
      </text>
    )
  }

  // D4 symmetric doctrine: too-small bars get an OUTSIDE label instead of silence.
  // Stacked segments keep suppression (opts.allowOutside false): an outside label inside a
  // stack would sit ON the neighbouring segment. Collision proxy: a slot narrower than
  // OUTSIDE_MIN_SLOT can't host a readable label without overlapping neighbours → tooltip-only.
  if (!(opts?.allowOutside ?? true)) return null
  if (width < OUTSIDE_MIN_SLOT) return null

  // y/height (and x/width) are normalized above regardless of which raw sign convention
  // recharts used, so y is always the visual top and y + height is always the visual bottom —
  // for both positive bars (grow up from the zero line) and negative bars (grow down from it).
  const isNeg = value < 0
  return (
    <text
      x={x + width / 2}
      y={isNeg ? y + height + OUTSIDE_GAP : y - OUTSIDE_GAP}
      textAnchor="middle"
      dominantBaseline={isNeg ? 'hanging' : 'auto'}
      fontSize={12}
      fontWeight={600}
      fill="#1a1a1a"
      style={{ filter: HALO_FILTER, pointerEvents: 'none' }}
    >
      {format(value)}
    </text>
  )
}

/** Horizontal-bar variant: recharts passes width = bar LENGTH, height = bar THICKNESS.
 *  Place the label just past the bar end, by sign — positive bars extend right of x so the
 *  label sits outside-right; negative bars extend left of x (the zero line at the bar's right
 *  edge) so the label sits outside-left, anchor end. Suppress only when the row is too thin or
 *  the value is missing. */
export function renderHorizontalBarValueLabel(
  props: LabelGeom,
  format: (n: number) => string,
): ReactElement | null {
  const xRaw = Number(props.x ?? 0)
  const y = Number(props.y ?? 0)
  const widthRaw = Number(props.width ?? 0)
  const height = Number(props.height ?? 0)
  // Same measured convention as renderBarValueLabel above: leftward (negative-value) bars pass
  // x at the value end with NEGATIVE width (FU-50 fix round 2026-08-04); normalize to the
  // visual left edge + magnitude so x + width is always the visual right edge.
  const width = Math.abs(widthRaw)
  const x = widthRaw < 0 ? xRaw + widthRaw : xRaw
  const rawValue = Number(props.value)
  const value =
    props.value != null && props.value !== '' && !Number.isNaN(rawValue) ? rawValue : undefined
  if (value === undefined) return null
  if (height < MIN_LABEL_HEIGHT) return null
  const isNeg = value < 0
  return (
    <text
      x={isNeg ? x - OUTSIDE_GAP : x + width + OUTSIDE_GAP}
      y={y + height / 2}
      textAnchor={isNeg ? 'end' : 'start'}
      dominantBaseline="central"
      fontSize={12}
      fontWeight={600}
      fill="#1a1a1a"
      style={{ filter: HALO_FILTER, pointerEvents: 'none' }}
    >
      {format(value)}
    </text>
  )
}
